class ProductFilter {
  constructor(options = {}) {
    this.filterSelector = options.filterSelector || '#product-category-filter';
    this.productSelector = options.productSelector || '.product-item';
    this.containerSelector = options.containerSelector || '.products-grid';
    this.lazyLoadInstance = options.lazyLoadInstance || null;
    
    this.filter = null;
    this.products = [];
    this.container = null;
    this.isCustomDropdown = false;
    this.dropdownButton = null;
    this.dropdownMenu = null;
    this.dropdownLabel = null;
    this.dropdownOptions = [];
    this.countDisplay = null;
    this.countNumber = null;
    this.countLabel = null;
    
    this.init();
  }

  init() {
    this.filter = document.querySelector(this.filterSelector);
    this.container = document.querySelector(this.containerSelector);
    
    if (!this.filter || !this.container) {
      return;
    }

    // Check if this is a custom dropdown (has wrapper)
    const wrapper = document.querySelector('#product-category-filter-wrapper');
    if (wrapper) {
      this.isCustomDropdown = true;
      this.initCustomDropdown();
    } else {
      // Native select dropdown
      this.filter.addEventListener('change', (e) => {
        this.handleFilterChange(e.target.value);
      });
    }

    // Get all product elements
    this.products = Array.from(document.querySelectorAll(this.productSelector));
    
    // Initialize product count display
    this.initProductCount();
  }
  
  initProductCount() {
    this.countDisplay = document.querySelector('.product-count-display');
    this.countNumber = document.querySelector('#product-count-number');
    this.countLabel = document.querySelector('#product-count-label');
    
    // Update count on initial load
    this.updateProductCount();
  }
  
  updateProductCount() {
    if (!this.countNumber || !this.countLabel) {
      return;
    }
    
    const visibleCount = this.products.filter((product) => {
      return product.style.display !== 'none';
    }).length;
    
    this.countNumber.textContent = visibleCount;
    
    // Update label for singular/plural
    if (visibleCount === 1) {
      this.countLabel.textContent = ' produkt';
    } else {
      this.countLabel.textContent = ' produkter';
    }
  }

  initCustomDropdown() {
    this.dropdownButton = document.querySelector('#product-category-filter-button');
    this.dropdownMenu = document.querySelector('#product-category-filter-dropdown');
    this.dropdownLabel = document.querySelector('.product-category-filter-label');
    this.dropdownOptions = Array.from(document.querySelectorAll('.product-category-filter-option'));

    if (!this.dropdownButton || !this.dropdownMenu || !this.dropdownLabel) {
      return;
    }

    // Toggle dropdown on button click
    this.dropdownButton.addEventListener('click', (e) => {
      e.stopPropagation();
      this.toggleDropdown();
    });

    // Handle option clicks
    this.dropdownOptions.forEach((option) => {
      option.addEventListener('click', (e) => {
        e.stopPropagation();
        const value = option.getAttribute('data-value') || '';
        this.selectOption(value, option.textContent.trim());
        this.closeDropdown();
      });
    });

    // Close dropdown when clicking outside
    document.addEventListener('click', (e) => {
      if (!e.target.closest('#product-category-filter-wrapper')) {
        this.closeDropdown();
      }
    });

    // Close dropdown on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isDropdownOpen()) {
        this.closeDropdown();
      }
    });
  }

  toggleDropdown() {
    const isOpen = this.isDropdownOpen();
    if (isOpen) {
      this.closeDropdown();
    } else {
      this.openDropdown();
    }
  }

  openDropdown() {
    if (this.dropdownMenu && this.dropdownButton) {
      this.dropdownMenu.classList.remove('opacity-0', 'invisible', 'translate-y-[-8px]');
      this.dropdownMenu.classList.add('opacity-100', 'visible', 'translate-y-0');
      this.dropdownButton.setAttribute('aria-expanded', 'true');
      this.dropdownButton.querySelector('.product-category-filter-arrow')?.classList.add('rotate-180');
    }
  }

  closeDropdown() {
    if (this.dropdownMenu && this.dropdownButton) {
      this.dropdownMenu.classList.remove('opacity-100', 'visible', 'translate-y-0');
      this.dropdownMenu.classList.add('opacity-0', 'invisible', 'translate-y-[-8px]');
      this.dropdownButton.setAttribute('aria-expanded', 'false');
      this.dropdownButton.querySelector('.product-category-filter-arrow')?.classList.remove('rotate-180');
    }
  }

  isDropdownOpen() {
    return this.dropdownMenu?.classList.contains('opacity-100') || false;
  }

  selectOption(value, label) {
    // Update hidden input value
    if (this.filter) {
      this.filter.value = value;
      
      // Trigger change event for compatibility
      const changeEvent = new Event('change', { bubbles: true });
      this.filter.dispatchEvent(changeEvent);
    }

    // Update button label
    if (this.dropdownLabel) {
      this.dropdownLabel.textContent = label || 'Alle kategorier';
    }

    // Update selected state on options
    this.dropdownOptions.forEach((option) => {
      if (option.getAttribute('data-value') === value) {
        option.classList.add('opacity-80', 'font-medium');
      } else {
        option.classList.remove('opacity-80', 'font-medium');
      }
    });

    // Handle filter change
    this.handleFilterChange(value);
  }

  handleFilterChange(selectedCategory) {
    const category = selectedCategory.trim();
    
    this.products.forEach((product) => {
      const productCategory = (product.getAttribute('data-category') || '').trim();
      
      if (!category || category === '') {
        // Show all products
        this.showProduct(product);
      } else if (productCategory === category) {
        // Show matching product
        this.showProduct(product);
      } else {
        // Hide non-matching product
        this.hideProduct(product);
      }
    });

    // Update product count display
    this.updateProductCount();
    
    // Re-observe visible products with IntersectionObserver
    // This allows them to fade in naturally when entering viewport
    if (this.lazyLoadInstance && this.lazyLoadInstance.observer) {
      const visibleProducts = this.products.filter((product) => {
        return product.style.display !== 'none';
      });
      
      // Helper to check if element is visible in viewport (any part visible)
      const isInViewport = (element) => {
        const rect = element.getBoundingClientRect();
        return (
          rect.top < (window.innerHeight || document.documentElement.clientHeight) &&
          rect.bottom > 0 &&
          rect.left < (window.innerWidth || document.documentElement.clientWidth) &&
          rect.right > 0
        );
      };
      
      // Use requestAnimationFrame to batch DOM updates
      requestAnimationFrame(() => {
        visibleProducts.forEach((product, index) => {
          // Reset to initial state if not already
          if (!product.classList.contains('opacity-0')) {
            product.classList.add('opacity-0', 'translate-y-4');
            product.classList.remove('opacity-100', 'translate-y-0');
          }
          
          // If product is already in viewport, animate immediately with minimal delay
          if (isInViewport(product)) {
            const delay = index * 20; // Very short delay for stagger
            setTimeout(() => {
              product.classList.remove('opacity-0', 'translate-y-4');
              product.classList.add('opacity-100', 'translate-y-0');
            }, delay);
          } else {
            // Re-observe products outside viewport - they'll animate when scrolled into view
            this.lazyLoadInstance.observer.unobserve(product);
            this.lazyLoadInstance.observer.observe(product);
          }
        });
      });
    }
  }

  showProduct(product) {
    product.style.display = '';
    
    // Reset product to initial state so it can be observed by IntersectionObserver again
    // This allows products to fade in when they enter the viewport
    if (!product.classList.contains('opacity-0')) {
      product.classList.add('opacity-0', 'translate-y-4');
      product.classList.remove('opacity-100', 'translate-y-0');
    }
  }

  hideProduct(product) {
    product.style.display = 'none';
    
    // Unobserve hidden products to prevent unnecessary checks
    if (this.lazyLoadInstance && this.lazyLoadInstance.observer) {
      this.lazyLoadInstance.observer.unobserve(product);
    }
  }

  // Method to set lazy load instance after initialization
  setLazyLoadInstance(lazyLoadInstance) {
    this.lazyLoadInstance = lazyLoadInstance;
  }

  // Method to refresh after dynamic content changes
  refresh() {
    this.products = Array.from(document.querySelectorAll(this.productSelector));
  }
}

export default ProductFilter;

